import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import MapView from '../../components/common/MapView';
import Modal from '../../components/common/Modal';
import ShareAccessOptions from '../../components/common/ShareAccessOptions';
import ConversionProgressBar from '../../components/common/ConversionProgressBar';
import { useConversionProgress, type ConversionProgress } from '../../hooks/useConversionProgress';
import { PROJECT_QUERY, PROJECT_FILES_QUERY } from '../../graphql/queries';
import { CREATE_SHARED_LINK_MUTATION, LOG_PROJECT_OPEN_MUTATION, SET_PROJECT_MATTERPORT_MUTATION } from '../../graphql/mutations';
import { truncateName } from '../../utils/text';
import { DEFAULT_SHARE_OPTIONS, shareOptionsInput } from '../../utils/shareLink';
import WalkthroughsMenu from './WalkthroughsMenu';
import { accessOf, sectionAllowed, type FeatureAccess } from '../../utils/access';
import { buildSceneMenu, type SceneChipKey, type SceneMenuEntry, type SceneMenuFile } from '../../utils/sceneMenu';
import WalkthroughCards from './WalkthroughCards';
import styles from './Project.module.css';
import { isSuperAdmin, seesAllProjects } from '../../utils/staff';
import { canEditProject, managesProject, projectRoleFor } from '../../utils/projectRole';
import { useDocumentFormats } from '../../hooks/useDocumentFormats';
import { documentFormatsLabel, documentOpensIn, documentViewerRoute } from '../../utils/fileFormats';
import { downloadProjectFile } from '../../utils/fileDownload';
import Icon, { type IconName } from '../../components/common/Icon';

// ─── Config ──────────────────────────────────────────────────────────────────

interface SectionConfig {
  path: string;
  fileType: string;
  viewerType: string;
  icon: IconName;
  label: string;
  desc: string;
  accentColor: string;
  iconBg: string;
  iconColor: string;
}

const SHARE_PASSWORD_MIN = 4;

const SECTIONS: SectionConfig[] = [
  { path: '2d',          fileType: 'two_d',       viewerType: 'viewer-2d',    icon: 'architecture',  label: '2D Drawings',    desc: 'DWG, CAD',      accentColor: '#3b82f6', iconBg: '#eff6ff', iconColor: '#2563eb' },
  { path: '3d',          fileType: 'three_d',     viewerType: 'viewer-3d-v2', icon: 'view_in_ar',    label: '3D BIM Models',  desc: 'IFC, RVT, RCS',      accentColor: '#8b5cf6', iconBg: '#f5f3ff', iconColor: '#7c3aed' },
  { path: 'documents',   fileType: 'document',    viewerType: 'viewer-pdf',   icon: 'description',   label: 'Documents',      desc: 'PDF',           accentColor: '#ef4444', iconBg: '#fef2f2', iconColor: '#dc2626' },
  { path: 'point-cloud', fileType: 'point_cloud', viewerType: 'viewer-cloud', icon: 'grain',         label: 'Point Cloud',    desc: 'E57, LAS, LAZ, RCP, LGSX', accentColor: '#f97316', iconBg: '#fff7ed', iconColor: '#ea580c' },
  { path: 'gaussian',    fileType: 'gaussian',    viewerType: 'viewer-splat', icon: 'blur_on',       label: 'Gaussian Splats', desc: 'Photoreal 3D',  accentColor: '#6366f1', iconBg: '#eef2ff', iconColor: '#4f46e5' },
  { path: 'orthomosaic', fileType: 'orthomosaic', viewerType: 'viewer-ortho', icon: 'satellite_alt', label: 'Orthomosaic',    desc: 'GeoTIFF',       accentColor: '#0ea5e9', iconBg: '#f0f9ff', iconColor: '#0284c7' },
  { path: 'video360',    fileType: 'video360',    viewerType: 'viewer-360',   icon: 'panorama_photosphere', label: '360 Videos', desc: 'MP4, MOV (equirectangular)', accentColor: '#d97706', iconBg: '#fef3e2', iconColor: '#b45309' },
];

// ─── Scene entry ─────────────────────────────────────────────────────────────

interface SceneEntryProps {
  projectId: string;
  files: SceneMenuFile[];
  access: FeatureAccess;
}

// One button for the whole 3D scene (point clouds, panoramas, BIM, splats,
// ortho); the caret offers a different starting configuration of the SAME
// page — the scene reads them from the query string.
const CHIP_CLASS: Record<SceneChipKey, string> = {
  cloud: styles.sceneChipCloud, pano: styles.sceneChipPano, bim: styles.sceneChipBim,
  dwg: styles.sceneChipDwg, splat: styles.sceneChipSplat, ortho: styles.sceneChipOrtho,
};

const SceneEntry = ({ projectId, files, access }: SceneEntryProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const menu = buildSceneMenu(files, access);
  const go = (query: string) => {
    setOpen(false);
    navigate(`/projects/${projectId}/scene${query ? `?${query}` : ''}`);
  };
  const bases = menu.entries.filter((e) => !e.drawing);
  const drawings = menu.entries.filter((e) => e.drawing);
  const renderEntry = (e: SceneMenuEntry) => (
    <button
      key={e.key}
      className={`${styles.sceneItem} ${e.isDefault ? styles.sceneItemDefault : ''}`}
      role="menuitem"
      onClick={() => go(e.query)}
    >
      <i className={styles.sceneDot} style={{ background: e.dot }} />
      <span>
        <b>{e.label}</b>
        <small title={e.drawing ? e.hint : undefined}>{e.drawing ? truncateName(e.hint) : e.hint}</small>
      </span>
    </button>
  );

  return (
    <div ref={wrapRef} className={styles.sceneEntry}>
      <div className={styles.sceneSplit}>
        <button className={styles.sceneMain} onClick={() => go(menu.defaultQuery)}>
          <Icon name="layers" className="text-base" />
          Open Scene
        </button>
        <button
          className={styles.sceneCaret}
          onClick={() => setOpen((o) => !o)}
          title="More ways to open"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Icon name={open ? 'expand_less' : 'expand_more'} className="text-base" />
        </button>
      </div>
      {open && (
        <div className={styles.sceneMenu} role="menu">
          {menu.chips.length > 0 && (
            <div className={styles.sceneMenuStrip}>
              <p className={styles.sceneMenuLabel}>In this project's scene</p>
              <div className={styles.sceneChips}>
                {menu.chips.map((c) => <span key={c.key} className={`${styles.sceneChip} ${CHIP_CLASS[c.key]}`}>{c.label}</span>)}
              </div>
            </div>
          )}
          {bases.map(renderEntry)}
          {drawings.length > 0 && <div className={styles.sceneMenuRule} />}
          {drawings.map(renderEntry)}
        </div>
      )}
    </div>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatStorage = (bytes: number): string => {
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// ─── Status badge ─────────────────────────────────────────────────────────────

interface StatusBadgeProps { status: string }

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const cfg: Record<string, { label: string; cls: string }> = {
    ready:      { label: 'Ready',      cls: styles.statusReady },
    processing: { label: 'Converting', cls: styles.statusProcessing },
    uploading:  { label: 'Uploading',  cls: styles.statusUploading },
    error:      { label: 'Error',      cls: styles.statusError },
  };
  const { label, cls } = cfg[status] ?? { label: status, cls: styles.statusError };
  return <span className={`${styles.statusBadge} ${cls}`}>{label}</span>;
};

// ─── File row ─────────────────────────────────────────────────────────────────

interface FileRowProps {
  file: {
    id: string;
    originalName: string;
    fileSize: number;
    extension: string;
    status: string;
    createdAt: string;
    uploadedBy: { firstName: string; lastName: string };
  };
  viewerType: string;
  projectId: string;
  progress?: ConversionProgress;
  downloadOnly?: boolean;
}

const FileRow = ({ file, viewerType, projectId, progress, downloadOnly = false }: FileRowProps) => {
  const navigate = useNavigate();
  const canView = file.status === 'ready';

  return (
    <tr className={styles.fileRow}>
      <td className={styles.fileNameCell}>
        <span className={styles.fileExt}>.{file.extension}</span>
        <span className={styles.fileName} title={file.originalName}>{file.originalName}</span>
      </td>
      <td className={styles.fileCellSize}>{formatSize(file.fileSize)}</td>
      <td className={styles.fileCellAction}>
        {file.status === 'processing' && progress ? (
          <ConversionProgressBar progress={progress} />
        ) : downloadOnly ? (
          <button
            className={styles.viewButton}
            disabled={!canView}
            onClick={() => canView && downloadProjectFile(file)}
            title={canView ? 'Download' : 'File not ready'}
          >
            <Icon name="download" style={{ fontSize: 16 }} />
            Download
          </button>
        ) : (
          <button
            className={styles.viewButton}
            disabled={!canView}
            onClick={() => canView && navigate(`/projects/${projectId}/${
              // RCS has no self-hosted pipeline — it still opens in the Autodesk viewer.
              viewerType === 'viewer-3d-v2' && (file.extension || '').toLowerCase() === 'rcs' ? 'viewer-3d' : viewerType
            }/${file.id}`)}
            title={canView ? 'Open viewer' : 'File not ready'}
          >
            <Icon name="open_in_new" style={{ fontSize: 16 }} />
            View
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Section card ─────────────────────────────────────────────────────────────

interface SectionCardProps {
  section: SectionConfig;
  projectId: string;
}

const SectionCard = ({ section, projectId }: SectionCardProps) => {
  const navigate = useNavigate();
  const documentFormats = useDocumentFormats();

  const { data, loading, refetch } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, type: section.fileType, page: 1, limit: 10 },
    skip: !projectId,
  });

  const files = data?.projectFiles?.files ?? [];
  const total: number = data?.projectFiles?.total ?? 0;

  const processingIds = section.fileType === 'point_cloud' || section.fileType === 'three_d'
    ? files.filter((f: { status: string }) => f.status === 'processing').map((f: { id: string }) => f.id)
    : [];
  const progressMap = useConversionProgress(processingIds, () => { refetch(); });

  return (
    <div className={styles.sectionCard}>
      {/* Header */}
      <div className={styles.sectionHeader}>
        <div className={styles.sectionHeaderLeft}>
          <div className={styles.sectionIconWrap} style={{ background: section.iconBg }}>
            <Icon name={section.icon} style={{ fontSize: 20, color: section.iconColor }} />
          </div>
          <div>
            <p className={styles.sectionLabel}>{section.label}</p>
            <p className={styles.sectionDesc}>{section.desc}</p>
          </div>
          {total > 0 && (
            <span className={styles.sectionCount} style={{ background: section.iconBg, color: section.iconColor }}>
              {total}
            </span>
          )}
        </div>
        <button
          className={styles.viewAllButton}
          style={{ color: section.accentColor }}
          onClick={() => navigate(`/projects/${projectId}/${section.path}`)}
        >
          View all
          <Icon name="chevron_right" style={{ fontSize: 16 }} />
        </button>
      </div>

      {/* File list */}
      {loading ? (
        <div className={styles.sectionLoading}>
          <Icon name="refresh" className="animate-spin" style={{ fontSize: 18, color: '#aaa' }} />
          <span>Loading…</span>
        </div>
      ) : files.length === 0 ? (
        <div className={styles.sectionEmpty}>
          <Icon name={section.icon} style={{ fontSize: 32, color: '#d1d5db' }} />
          <p>No {section.label.toLowerCase()} yet</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.fileTable}>
            <thead>
              <tr>
                <th className={styles.fileThName}>File</th>
                <th className={styles.fileTh}>Size</th>
                <th className={styles.fileTh} />
              </tr>
            </thead>
            <tbody>
              {files.map((file: Parameters<typeof FileRow>[0]['file']) => {
                const docRoute = section.fileType === 'document' ? documentViewerRoute(documentOpensIn(file.extension, documentFormats)) : undefined;
                return (
                  <FileRow
                    key={file.id}
                    file={file}
                    viewerType={docRoute ?? section.viewerType}
                    projectId={projectId}
                    progress={progressMap[file.id]}
                    downloadOnly={docRoute === null}
                  />
                );
              })}
            </tbody>
          </table>
          {total > 10 && (
            <div className={styles.moreRow}>
              <button
                className={styles.moreButton}
                style={{ color: section.accentColor }}
                onClick={() => navigate(`/projects/${projectId}/${section.path}`)}
              >
                +{total - 10} more — view all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const Project = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const documentFormats = useDocumentFormats();

  const [showShare, setShowShare]   = useState(false);
  // 'tour' shows the embedded walkthrough; 'edit' the link input.
  const [matterportModal, setMatterportModal] = useState<'tour' | 'edit' | null>(null);
  const [matterportInput, setMatterportInput] = useState('');
  const [matterportError, setMatterportError] = useState('');
  const [permission, setPermission] = useState<'view' | 'download' | 'comment'>('view');
  const [expiresAt, setExpiresAt]   = useState('');
  const [maxViews, setMaxViews]     = useState('');
  const [note, setNote]             = useState('');
  const [sharePassword, setSharePassword] = useState('');
  const [shareError, setShareError] = useState('');
  const [shareOptions, setShareOptions] = useState(DEFAULT_SHARE_OPTIONS);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied]         = useState(false);

  const [logProjectOpen] = useMutation(LOG_PROJECT_OPEN_MUTATION);

  useEffect(() => {
    if (!projectId || seesAllProjects(user)) return;
    logProjectOpen({ variables: { projectId } }).catch(() => {/* non-fatal */});
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [createSharedLink, { loading: sharing }] = useMutation(CREATE_SHARED_LINK_MUTATION, {
    onCompleted: (data) => {
      const token = data.createSharedLink.token;
      setGeneratedLink(`${window.location.origin}/share/${token}`);
    },
    onError: (e) => setShareError(e.message),
  });

  const handleShare = () => {
    setShareError('');
    createSharedLink({
      variables: {
        input: {
          projectId,
          permission,
          // The date input yields "YYYY-MM-DD", but the DateTime scalar requires a
          // full ISO date-time. Expire at the END of the chosen day, local time —
          // a link "valid until 24 Jul" should work throughout 24 Jul.
          expiresAt:  expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : undefined,
          maxViews:   maxViews   ? parseInt(maxViews) : undefined,
          note:       note       || undefined,
          password:   sharePassword || undefined,
          ...shareOptionsInput(shareOptions),
        },
      },
    });
  };

  const sharePasswordTooShort = sharePassword.length > 0 && sharePassword.length < SHARE_PASSWORD_MIN;

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeShare = () => {
    setShowShare(false);
    setGeneratedLink('');
    setPermission('view');
    setExpiresAt('');
    setMaxViews('');
    setNote('');
    setSharePassword('');
    setShareError('');
    setShareOptions(DEFAULT_SHARE_OPTIONS);
    setCopied(false);
  };

  const access = accessOf(user);
  const sceneAllowed = access.scene;
  const { data: sceneFilesData } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, page: 1, limit: 500 },
    skip: !projectId || !sceneAllowed,
  });

  const { data, loading } = useQuery(PROJECT_QUERY, {
    variables: { id: projectId },
    skip: !projectId,
  });

  const project = data?.project;
  const myRole = projectRoleFor(project, user);
  const canEditMatterport = !!project && managesProject(myRole);

  const [saveMatterport, { loading: savingMatterport }] = useMutation(SET_PROJECT_MATTERPORT_MUTATION, {
    onCompleted: (res) => {
      setMatterportError('');
      setMatterportModal(res?.setProjectMatterport?.matterportUrl ? 'tour' : null);
    },
    onError: (err) => setMatterportError(err.message),
  });

  const submitMatterport = () => {
    // Mirror of the server's extraction so bad pastes fail fast, client-side.
    const raw = matterportInput.trim();
    const m = raw.match(/[?&]m=([A-Za-z0-9]+)/) || raw.match(/^([A-Za-z0-9]{8,})$/);
    if (raw && !m) {
      setMatterportError('Paste a Matterport link, embed code, or model id');
      return;
    }
    saveMatterport({ variables: { projectId, url: raw || null } });
  };

  return (
    <DashboardLayout sidebarContext="project" projectId={projectId}>
      {loading ? (
        <div className="flex items-center justify-center py-16 text-on-surface-variant">
          <Icon name="refresh" className="animate-spin mr-2" />
          Loading project...
        </div>
      ) : !project ? (
        <div className="text-center py-16">
          <p className="text-on-surface-variant">Project not found</p>
          <button onClick={() => navigate('/projects')} className="mt-4 text-primary hover:underline text-sm">
            Back to projects
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Breadcrumb + title */}
          <div className={`flex justify-between items-start ${styles.pageHeader}`}>
            <div>
              <div className="flex items-center gap-2 text-xs text-on-surface-variant mb-2">
                <button onClick={() => navigate('/projects')} className="hover:text-primary transition-colors">Projects</button>
                <Icon name="chevron_right" className="text-sm" />
                <span title={project.name}>{truncateName(project.name)}</span>
              </div>
              <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">{project.name}</h2>
              {project.location && (
                <p className="text-on-surface-variant mt-1 flex items-center gap-1">
                  <Icon name="location_on" className="text-sm" />
                  {project.location}
                </p>
              )}
            </div>
            <div className={`flex items-center gap-3 ${styles.headerActions}`}>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => navigate(`/projects/${projectId}/settings`)}
                  className="p-2 text-on-surface-variant hover:bg-surface-container-low rounded-lg transition-colors"
                  title="Project settings"
                  aria-label="Project settings"
                >
                  <Icon name="settings" />
                </button>
                {canEditProject(myRole) && (
                  <button
                    onClick={() => setShowShare(true)}
                    className="p-2 text-on-surface-variant hover:bg-surface-container-low rounded-lg transition-colors"
                    title="Share project"
                    aria-label="Share project"
                  >
                    <Icon name="share" />
                  </button>
                )}
              </div>
              <WalkthroughsMenu
                projectId={projectId!}
                matterportUrl={project?.matterportUrl}
                matterportAllowed={access.matterport}
                canEditMatterport={canEditMatterport}
                videosAllowed={access.video360}
                onOpenMatterport={() => setMatterportModal('tour')}
                onEditMatterport={() => { setMatterportInput(''); setMatterportError(''); setMatterportModal('edit'); }}
              />
              {sceneAllowed && (
                <SceneEntry
                  projectId={projectId!}
                  files={sceneFilesData?.projectFiles?.files ?? []}
                  access={access}
                />
              )}
            </div>
          </div>

          {/* Stats row + map */}
          <div className={styles.statsMapRow}>
            <div className="grid grid-cols-2 gap-4">
              {([
                { icon: 'description',  label: 'Total Files',   value: project.fileCount.toString(),                     color: 'text-primary',            bg: 'bg-primary/10' },
                { icon: 'hard_drive',   label: 'Storage Used',  value: formatStorage(project.storageUsed),              color: 'text-tertiary',           bg: 'bg-tertiary/10' },
                { icon: 'groups',       label: 'Team Members',  value: project.members.length.toString(),                color: 'text-secondary',          bg: 'bg-secondary-container/30' },
                { icon: 'schedule',     label: 'Last Updated',  value: new Date(project.updatedAt).toLocaleDateString(), color: 'text-on-surface-variant', bg: 'bg-surface-container-high' },
              ] as const).map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-5">
                  <div className={`p-2 ${stat.bg} rounded-lg ${stat.color} w-fit mb-3`}>
                    <Icon name={stat.icon} />
                  </div>
                  <p className="text-xs text-on-surface-variant uppercase tracking-wider font-medium">{stat.label}</p>
                  <p className="text-xl font-bold mt-1 font-headline">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className={styles.mapPanel}>
              {project.overviewPanel === 'walkthroughs' ? (
                <WalkthroughCards
                  projectId={projectId!}
                  matterportUrl={project.matterportUrl}
                  matterportAllowed={access.matterport}
                  canEditMatterport={canEditMatterport}
                  videosAllowed={access.video360}
                  onOpenMatterport={() => setMatterportModal('tour')}
                  onEditMatterport={() => { setMatterportInput(''); setMatterportError(''); setMatterportModal('edit'); }}
                />
              ) : project.latitude != null && project.longitude != null ? (
                <MapView lat={project.latitude} lng={project.longitude} label={project.name} />
              ) : (
                <div className={styles.noLocation}>
                  <Icon name="location_off" style={{ fontSize: 32, color: '#d1d5db' }} />
                  <p>No location set</p>
                  <button onClick={() => navigate(`/projects/${projectId}/settings`)} className={styles.setLocationBtn}>
                    Set location in settings
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Project Sections */}
          <div>
            <h3 className="text-lg font-bold text-on-surface font-headline mb-4">Project Sections</h3>
            <div className={styles.sectionsStack}>
              {SECTIONS.map((section) => {
                // LGSX upload is permission-gated (admin -> Users -> Edit
                // permissions) — don't advertise it to users who can't use it.
                const canLgsx = user?.permissions?.lgsxUpload || isSuperAdmin(user);
                if (!sectionAllowed(section.path, access)) return null;
                const shown = (section.path === 'point-cloud' && !canLgsx)
                  ? { ...section, desc: section.desc.replace(/,\s*LGSX/, '') }
                  : section.path === 'documents' ? { ...section, desc: documentFormatsLabel(documentFormats) }
                  : section;
                return <SectionCard key={section.path} section={shown} projectId={projectId!} />;
              })}
            </div>
          </div>

          {/* Description */}
          {project.description && (
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-sm font-bold text-on-surface-variant uppercase tracking-wider mb-3">Description</h3>
              <p className="text-sm text-on-surface leading-relaxed">{project.description}</p>
            </div>
          )}

          {/* Team */}
          <div className="bg-white rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-on-surface-variant uppercase tracking-wider">Team Members</h3>
              <button
                onClick={() => navigate('/team')}
                className="text-xs text-primary font-semibold hover:underline flex items-center gap-1"
              >
                Manage <Icon name="chevron_right" className="text-sm" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-container flex items-center justify-center text-white text-xs font-bold">
                  {project.owner.firstName[0]}{project.owner.lastName[0]}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-bold">{project.owner.firstName} {project.owner.lastName}</p>
                  <p className="text-xs text-on-surface-variant">{project.owner.email}</p>
                </div>
                <span className="text-[10px] font-bold text-primary bg-primary/10 px-2 py-1 rounded-full">Owner</span>
              </div>
              {project.members.slice(0, 4).map((m: { user: { id: string; firstName: string; lastName: string; email: string }; role: string }) => (
                <div key={m.user.id} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-surface-container-highest flex items-center justify-center text-on-surface text-xs font-bold">
                    {m.user.firstName[0]}{m.user.lastName[0]}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold">{m.user.firstName} {m.user.lastName}</p>
                    <p className="text-xs text-on-surface-variant">{m.user.email}</p>
                  </div>
                  <span className="text-[10px] font-bold text-on-surface-variant bg-surface-container px-2 py-1 rounded-full capitalize">{m.role}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <Modal
        isOpen={matterportModal === 'tour'}
        onClose={() => setMatterportModal(null)}
        title="Matterport tour"
        maxWidth="920px"
        footer={
          canEditMatterport ? (
            <button
              className="px-5 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors"
              onClick={() => { setMatterportInput(project?.matterportUrl ?? ''); setMatterportError(''); setMatterportModal('edit'); }}
            >
              Change link
            </button>
          ) : undefined
        }
      >
        {project?.matterportUrl && (
          <iframe
            className={styles.matterportFrame}
            src={project.matterportUrl}
            title="Matterport tour"
            allowFullScreen
            allow="autoplay; fullscreen; web-share; xr-spatial-tracking;"
          />
        )}
      </Modal>

      <Modal
        isOpen={matterportModal === 'edit'}
        onClose={() => setMatterportModal(null)}
        title="Matterport tour link"
        footer={
          <div className="flex items-center gap-3">
            {project?.matterportUrl && (
              <button
                className="px-5 py-2 text-sm font-medium text-error hover:bg-surface-container rounded-lg transition-colors"
                disabled={savingMatterport}
                onClick={() => { setMatterportInput(''); saveMatterport({ variables: { projectId, url: null } }); }}
              >
                Remove
              </button>
            )}
            <button
              className="btn-primary px-5 py-2 rounded-lg text-sm"
              disabled={savingMatterport || !matterportInput.trim()}
              onClick={submitMatterport}
            >
              {savingMatterport ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        <p className="text-sm text-on-surface-variant mb-3">
          Paste the Matterport share link, the embed code, or just the model id.
        </p>
        <input
          aria-label="Matterport link"
          className="w-full px-4 py-2.5 rounded-lg border border-outline-variant bg-surface text-sm text-on-surface"
          placeholder="https://my.matterport.com/show/?m=cwzsshp39jB"
          value={matterportInput}
          onChange={(e) => { setMatterportInput(e.target.value); setMatterportError(''); }}
        />
        {matterportError && <p className="text-sm text-error mt-2">{matterportError}</p>}
      </Modal>

      <Modal
        isOpen={showShare}
        onClose={closeShare}
        title="Share Project"
        footer={
          generatedLink ? (
            <button onClick={closeShare} className="px-5 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">
              Close
            </button>
          ) : (
            <>
              <button onClick={closeShare} className="px-4 py-2 text-sm font-medium text-on-surface-variant hover:bg-surface-container rounded-lg transition-colors">
                Cancel
              </button>
              <button
                onClick={handleShare}
                disabled={sharing || sharePasswordTooShort}
                className="btn-primary px-5 py-2 rounded-lg text-sm bg-gradient-to-r from-primary to-primary-container disabled:opacity-50"
              >
                {sharing ? 'Generating…' : 'Generate Link'}
              </button>
            </>
          )
        }
      >
        {generatedLink ? (
          <div className="space-y-4">
            <p className="text-sm text-on-surface-variant">
              {sharePassword
                ? 'Your shareable link is ready. Recipients need both this link and the password to access the project.'
                : 'Your shareable link is ready. Anyone with this link can access the project.'}
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                aria-label="Share link"
                value={generatedLink}
                className="input-field flex-1 font-mono text-xs"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 rounded-lg text-sm font-semibold border border-outline-variant hover:bg-surface-container transition-colors whitespace-nowrap"
              >
                {copied ? '✓ Copied' : 'Copy'}
              </button>
            </div>
            {sharePassword && (
              <p className="text-xs text-on-surface-variant">
                Password: <span className="font-mono text-on-surface">{sharePassword}</span> — it cannot be viewed again after you close this window.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label htmlFor="share-expires" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Expires (optional)</label>
                <input
                  id="share-expires"
                  type="date"
                  className="input-field"
                  value={expiresAt}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label htmlFor="share-max-views" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Max views (optional)</label>
                <input
                  id="share-max-views"
                  type="number"
                  className="input-field"
                  placeholder="Unlimited"
                  min={1}
                  value={maxViews}
                  onChange={(e) => setMaxViews(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="share-note" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Note (optional)</label>
              <input
                id="share-note"
                className="input-field"
                placeholder="e.g. For client review"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="share-password" className="text-xs font-bold uppercase tracking-wider text-on-surface-variant">Password (optional)</label>
              <input
                id="share-password"
                type="text"
                className="input-field"
                placeholder="Leave empty for no password"
                autoComplete="off"
                spellCheck={false}
                maxLength={64}
                value={sharePassword}
                onChange={(e) => setSharePassword(e.target.value)}
              />
              <p className={`text-xs ${sharePasswordTooShort ? 'text-error' : 'text-on-surface-variant'}`}>
                {sharePasswordTooShort
                  ? `Use at least ${SHARE_PASSWORD_MIN} characters`
                  : 'Recipients must enter it to open the link.'}
              </p>
            </div>

            <ShareAccessOptions value={shareOptions} onChange={setShareOptions} />
            {shareError && <p className="text-sm text-error">{shareError}</p>}
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default Project;
